import React, { useState } from 'react';
import styled from 'styled-components';
import { theme, neutral, blueGrey, jadeGreen, lightBlue, volcano } from '../../design-system/theme';
import { InfographicViewer } from './InfographicViewer';
import {
  GLOBAL_PRIVACY_LAWS_SYNTAX,
} from './infographicData';

// ===== STYLED COMPONENTS =====

const Container = styled.div`
  padding: 0;
`;

const LawCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid ${neutral[3]};
`;

const LawHeader = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${props => props.$color};
`;

const FlagIcon = styled.span`
  font-size: 40px;
`;

const LawTitle = styled.div`
  flex: 1;
  
  h3 {
    margin: 0 0 4px 0;
    font-size: 22px;
    font-weight: 700;
    color: ${theme.textPrimary};
  }
  
  p {
    margin: 0;
    font-size: 14px;
    color: ${theme.textSecondary};
  }
`;

const StatusBadge = styled.span<{ $status: 'active' | 'draft' | 'upcoming' }>`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.$status) {
      case 'active':
        return `background: ${jadeGreen[1]}; color: ${jadeGreen[7]};`;
      case 'upcoming':
        return `background: ${lightBlue[1]}; color: ${lightBlue[7]};`;
      case 'draft':
        return `background: ${neutral[3]}; color: ${neutral[7]};`;
    }
  }}
`;

const KeyPointsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const KeyPoint = styled.div`
  background: ${neutral[1]};
  border-radius: 12px;
  padding: 16px;
  
  h4 {
    margin: 0 0 8px 0;
    font-size: 13px;
    font-weight: 600;
    color: ${theme.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: ${theme.textPrimary};
  }
`;

const ExpandableSection = styled.div`
  margin-top: 16px;
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${neutral[2]};
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.textPrimary};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${neutral[3]};
  }
  
  svg {
    transition: transform 0.2s;
  }
`;

const ExpandedContent = styled.div<{ $expanded: boolean }>`
  max-height: ${props => props.$expanded ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-top: ${props => props.$expanded ? '16px' : '0'};
`;

const RightsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
`;

const RightItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: ${neutral[1]};
  border-radius: 10px;
  
  .icon {
    font-size: 24px;
    flex-shrink: 0;
  }
  
  .content {
    h5 {
      margin: 0 0 4px 0;
      font-size: 15px;
      font-weight: 600;
      color: ${theme.textPrimary};
    }
    
    p {
      margin: 0;
      font-size: 13px;
      color: ${theme.textSecondary};
      line-height: 1.5;
    }
  }
`;

const InfoBox = styled.div<{ $type: 'info' | 'warning' | 'success' }>`
  padding: 16px 20px;
  border-radius: 12px;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  
  ${props => {
    switch (props.$type) {
      case 'info':
        return `background: ${lightBlue[1]}; border-left: 4px solid ${lightBlue[5]};`;
      case 'warning':
        return `background: #fff8e6; border-left: 4px solid #f59e0b;`;
      case 'success':
        return `background: ${jadeGreen[1]}; border-left: 4px solid ${jadeGreen[5]};`;
    }
  }}
  
  .icon {
    font-size: 20px;
    flex-shrink: 0;
  }
  
  .content {
    font-size: 14px;
    line-height: 1.6;
    color: ${theme.textPrimary};
    
    strong {
      font-weight: 600;
    }
  }
`;

const MinorsConsentCard = styled.div`
  background: #fef3f2;
  border-radius: 12px;
  padding: 20px;
  margin-top: 16px;
  border: 1px solid #fecaca;
`;

const MinorsConsentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  
  .icon {
    font-size: 24px;
  }
  
  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${theme.textPrimary};
  }
`;

const MinorsConsentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MinorsConsentStat = styled.div`
  background: white;
  border-radius: 8px;
  padding: 12px;
  
  .label {
    font-size: 11px;
    font-weight: 600;
    color: ${theme.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  
  .value {
    font-size: 14px;
    font-weight: 600;
    color: ${theme.textPrimary};
  }
`;

const MinorsRulesList = styled.ul`
  margin: 0;
  padding: 0 0 0 20px;
  
  li {
    font-size: 13px;
    color: ${theme.textSecondary};
    line-height: 1.6;
    margin-bottom: 6px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.textPrimary};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  .icon {
    font-size: 28px;
  }
`;

// ===== PRIVACY LAW DATA =====

interface MinorsConsent {
  ageOfConsent: string;
  parentalConsentRequired: string;
  verificationRequired: string;
  specialRules: string[];
}

interface PrivacyLaw {
  id: string;
  flag: string;
  name: string;
  fullName: string;
  region: string;
  status: 'active' | 'draft' | 'upcoming';
  effectiveDate: string;
  color: string;
  keyPoints: {
    consentAge: string;
    penalty: string;
    dpoRequired: string;
    consentType: string;
  };
  rights: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
  minorsConsent: MinorsConsent;
  platformSupport: string;
}

const privacyLaws: PrivacyLaw[] = [
  {
    id: 'dpdp',
    flag: '🇮🇳',
    name: 'DPDP Act 2023',
    fullName: 'Digital Personal Data Protection Act',
    region: 'India',
    status: 'active',
    effectiveDate: 'August 2023',
    color: volcano[5],
    keyPoints: {
      consentAge: '18 years',
      penalty: '₹250 Crore',
      dpoRequired: 'For Significant Data Fiduciaries',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Request summary of personal data and processing activities' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct inaccurate or incomplete personal data' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Request deletion of personal data no longer necessary' },
      { icon: '↩️', title: 'Right to Withdraw', description: 'Withdraw consent at any time' },
      { icon: '📢', title: 'Right to Grievance', description: 'File complaints with Data Fiduciary or Board' },
      { icon: '👤', title: 'Right to Nominate', description: 'Nominate person to exercise rights after death/incapacity' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - verifiable parental consent required for all minors',
      verificationRequired: 'Yes - must verify lawful guardian status',
      specialRules: [
        'Processing of children\'s data requires verifiable consent from lawful guardian',
        'No tracking, behavioral monitoring, or targeted advertising for children',
        'Data Fiduciaries must not process data likely to cause detrimental effect on children',
        'Higher penalties for violations involving children\'s data',
      ],
    },
    platformSupport: 'Full compliance with all DPDP requirements including consent collection, withdrawal management, and audit trails.',
  },
  {
    id: 'gdpr',
    flag: '🇪🇺',
    name: 'GDPR',
    fullName: 'General Data Protection Regulation',
    region: 'European Union',
    status: 'active',
    effectiveDate: 'May 2018',
    color: blueGrey[7],
    keyPoints: {
      consentAge: '16 years',
      penalty: '€20M or 4% turnover',
      dpoRequired: 'For large-scale processing',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Obtain confirmation and access to personal data' },
      { icon: '✏️', title: 'Right to Rectification', description: 'Correct inaccurate personal data' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Right to be forgotten under certain conditions' },
      { icon: '⏸️', title: 'Right to Restriction', description: 'Restrict processing of personal data' },
      { icon: '📦', title: 'Right to Portability', description: 'Receive data in structured, machine-readable format' },
      { icon: '🚫', title: 'Right to Object', description: 'Object to processing for direct marketing' },
    ],
    minorsConsent: {
      ageOfConsent: '16 years (member states can lower to 13)',
      parentalConsentRequired: 'Yes - for children below the age threshold',
      verificationRequired: 'Yes - reasonable efforts to verify parental consent',
      specialRules: [
        'Default age is 16, but EU member states can lower to minimum 13 years',
        'Parental consent must be verifiable with "reasonable efforts" considering available technology',
        'Privacy notice must be written in clear, plain language understandable by children',
        'Right to erasure strengthened for data collected during childhood',
        'Profiling of children for marketing purposes is prohibited',
      ],
    },
    platformSupport: 'GDPR-compliant templates, consent management, and data subject request handling.',
  },
  {
    id: 'ccpa',
    flag: '🇺🇸',
    name: 'CCPA/CPRA',
    fullName: 'California Consumer Privacy Act / California Privacy Rights Act',
    region: 'California, USA',
    status: 'active',
    effectiveDate: 'January 2020 / January 2023',
    color: jadeGreen[6],
    keyPoints: {
      consentAge: '16 years',
      penalty: '$7,500 per violation',
      dpoRequired: 'No',
      consentType: 'Opt-out model',
    },
    rights: [
      { icon: '🔍', title: 'Right to Know', description: 'Know what personal information is collected' },
      { icon: '🗑️', title: 'Right to Delete', description: 'Delete personal information held by businesses' },
      { icon: '🚫', title: 'Right to Opt-out', description: 'Opt-out of sale/sharing of personal information' },
      { icon: '⚖️', title: 'Right to Non-discrimination', description: 'Equal service regardless of privacy choices' },
      { icon: '✅', title: 'Right to Correct', description: 'Correct inaccurate personal information' },
      { icon: '🔒', title: 'Right to Limit Use', description: 'Limit use of sensitive personal information' },
    ],
    minorsConsent: {
      ageOfConsent: '16 years for opt-out, 13 for opt-in',
      parentalConsentRequired: 'Yes - for children under 13',
      verificationRequired: 'Yes - affirmative authorization required',
      specialRules: [
        'Children under 13: Parental opt-in consent required before selling personal information',
        'Children 13-16: Child can opt-in themselves, but cannot opt-out sale of their data',
        'Consumers 16+: Must provide opt-out mechanism for sale of personal information',
        'COPPA also applies: Federal law requires parental consent for under 13',
        'Constructive knowledge of child\'s age triggers compliance requirements',
      ],
    },
    platformSupport: 'CCPA disclosure templates and opt-out management capabilities.',
  },
  {
    id: 'lgpd',
    flag: '🇧🇷',
    name: 'LGPD',
    fullName: 'Lei Geral de Proteção de Dados',
    region: 'Brazil',
    status: 'active',
    effectiveDate: 'September 2020',
    color: '#009739',
    keyPoints: {
      consentAge: '18 years',
      penalty: '2% of revenue (max R$50M)',
      dpoRequired: 'Yes',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '✅', title: 'Confirmation', description: 'Confirm existence of processing' },
      { icon: '👁️', title: 'Access', description: 'Access personal data held' },
      { icon: '✏️', title: 'Correction', description: 'Correct incomplete or inaccurate data' },
      { icon: '🗑️', title: 'Deletion', description: 'Delete unnecessary data' },
      { icon: '📦', title: 'Portability', description: 'Transfer data to another provider' },
      { icon: '↩️', title: 'Revocation', description: 'Revoke consent at any time' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - specific consent from at least one parent or legal guardian',
      verificationRequired: 'Yes - best efforts to verify parental consent',
      specialRules: [
        'Processing children\'s data requires specific and prominent consent from parent/guardian',
        'Data processing must be in the child\'s best interest',
        'Controllers must make best efforts to verify that consent was given by appropriate person',
        'Information provided to children must be simple and accessible',
        'LGPD explicitly defines "child" and "adolescent" based on Brazilian Child and Adolescent Statute',
      ],
    },
    platformSupport: 'LGPD-compliant consent collection and data subject rights management.',
  },
  // ===== ASIA-PACIFIC =====
  {
    id: 'pdpa-singapore',
    flag: '🇸🇬',
    name: 'PDPA',
    fullName: 'Personal Data Protection Act',
    region: 'Singapore',
    status: 'active',
    effectiveDate: 'July 2014',
    color: '#EF3340',
    keyPoints: {
      consentAge: '18 years',
      penalty: 'S$1M or 10% turnover',
      dpoRequired: 'Yes (DPO required)',
      consentType: 'Opt-in with exceptions',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data and how it was used' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct errors in personal data' },
      { icon: '↩️', title: 'Right to Withdraw', description: 'Withdraw consent at any time' },
      { icon: '📦', title: 'Right to Portability', description: 'Transfer data to another organization' },
      { icon: '🚫', title: 'Do Not Call', description: 'Opt out of telemarketing calls/messages' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years (age of majority)',
      parentalConsentRequired: 'Yes - for minors under 18',
      verificationRequired: 'Yes - reasonable steps to verify',
      specialRules: [
        'Minors cannot give valid consent; parent/guardian consent required',
        'Organizations must take reasonable steps to verify guardian status',
        'Educational institutions have specific obligations for student data',
        'PDPA does not have specific child-focused provisions like GDPR',
      ],
    },
    platformSupport: 'PDPA-compliant consent workflows and Do Not Call registry integration.',
  },
  {
    id: 'pdpa-thailand',
    flag: '🇹🇭',
    name: 'PDPA Thailand',
    fullName: 'Personal Data Protection Act B.E. 2562',
    region: 'Thailand',
    status: 'active',
    effectiveDate: 'June 2022',
    color: '#00247D',
    keyPoints: {
      consentAge: '20 years',
      penalty: '฿5M + criminal penalties',
      dpoRequired: 'For specific cases',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access and obtain copy of personal data' },
      { icon: '✏️', title: 'Right to Rectification', description: 'Correct inaccurate or incomplete data' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Delete data when no longer necessary' },
      { icon: '⏸️', title: 'Right to Restriction', description: 'Restrict processing in certain cases' },
      { icon: '📦', title: 'Right to Portability', description: 'Transfer data in machine-readable format' },
      { icon: '🚫', title: 'Right to Object', description: 'Object to processing for direct marketing' },
    ],
    minorsConsent: {
      ageOfConsent: '20 years (Thai age of majority)',
      parentalConsentRequired: 'Yes - for minors and incompetent persons',
      verificationRequired: 'Yes - must verify parental authority',
      specialRules: [
        'Age of majority in Thailand is 20 years',
        'Minors aged 10+ with capacity may consent with guardian oversight',
        'Quasi-incompetent persons need curator consent',
        'Criminal penalties possible for violations involving minors',
      ],
    },
    platformSupport: 'Thailand PDPA consent templates and bilingual support.',
  },
  {
    id: 'appi-japan',
    flag: '🇯🇵',
    name: 'APPI',
    fullName: 'Act on Protection of Personal Information',
    region: 'Japan',
    status: 'active',
    effectiveDate: 'April 2022 (amended)',
    color: '#BC002D',
    keyPoints: {
      consentAge: '18 years',
      penalty: '¥100M or 1 year prison',
      dpoRequired: 'No (but recommended)',
      consentType: 'Opt-in for sensitive data',
    },
    rights: [
      { icon: '👁️', title: 'Right to Disclosure', description: 'Request disclosure of retained personal data' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct, add, or delete inaccurate data' },
      { icon: '⏹️', title: 'Right to Cessation', description: 'Cease use or delete data' },
      { icon: '🚫', title: 'Right to Stop Transfer', description: 'Stop third-party transfers' },
      { icon: '📋', title: 'Right to Explanation', description: 'Know purposes and third-party recipients' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years (lowered from 20 in 2022)',
      parentalConsentRequired: 'Yes - guardian consent for minors',
      verificationRequired: 'Yes - reasonable verification measures',
      specialRules: [
        'Age of majority lowered from 20 to 18 in April 2022',
        'Special care required personal information requires explicit consent',
        'Online services targeting children need enhanced verification',
        'Pseudonymized data rules provide some flexibility',
      ],
    },
    platformSupport: 'APPI-compliant consent management with Japanese language support.',
  },
  {
    id: 'pipl-china',
    flag: '🇨🇳',
    name: 'PIPL',
    fullName: 'Personal Information Protection Law',
    region: 'China',
    status: 'active',
    effectiveDate: 'November 2021',
    color: '#DE2910',
    keyPoints: {
      consentAge: '14 years',
      penalty: '¥50M or 5% turnover',
      dpoRequired: 'For large processors',
      consentType: 'Separate consent required',
    },
    rights: [
      { icon: '👁️', title: 'Right to Know', description: 'Know and decide on personal information processing' },
      { icon: '✏️', title: 'Right to Correction', description: 'Request correction of inaccurate information' },
      { icon: '🗑️', title: 'Right to Deletion', description: 'Delete information under certain conditions' },
      { icon: '📋', title: 'Right to Explanation', description: 'Request explanation of processing rules' },
      { icon: '📦', title: 'Right to Portability', description: 'Copy and transfer personal information' },
      { icon: '↩️', title: 'Right to Withdraw', description: 'Withdraw consent without affecting prior processing' },
    ],
    minorsConsent: {
      ageOfConsent: '14 years (strictest in major laws)',
      parentalConsentRequired: 'Yes - parental/guardian consent for under 14',
      verificationRequired: 'Yes - must verify consent authenticity',
      specialRules: [
        'Minors under 14 classified as sensitive personal information',
        'Separate consent required from parent/guardian',
        'Must formulate specific processing rules for children',
        'Enhanced security measures for minor data',
        'Strict cross-border transfer rules for minor data',
      ],
    },
    platformSupport: 'PIPL-compliant consent with data localization support.',
  },
  {
    id: 'pipa-korea',
    flag: '🇰🇷',
    name: 'PIPA',
    fullName: 'Personal Information Protection Act',
    region: 'South Korea',
    status: 'active',
    effectiveDate: 'September 2011 (amended 2023)',
    color: '#003478',
    keyPoints: {
      consentAge: '14 years',
      penalty: '₩5B or 4% revenue',
      dpoRequired: 'Yes (CPO required)',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal information held' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct or delete inaccurate data' },
      { icon: '⏹️', title: 'Right to Suspension', description: 'Suspend processing of personal information' },
      { icon: '🗑️', title: 'Right to Deletion', description: 'Request deletion of personal information' },
      { icon: '💰', title: 'Right to Compensation', description: 'Claim damages for privacy violations' },
    ],
    minorsConsent: {
      ageOfConsent: '14 years',
      parentalConsentRequired: 'Yes - legal representative consent for under 14',
      verificationRequired: 'Yes - must verify representative status',
      specialRules: [
        'Children under 14 require legal representative consent',
        'Must verify legal representative identity and relationship',
        'Separate provisions in Act on Promotion of ICT for online services',
        'Schools and educational institutions have special obligations',
      ],
    },
    platformSupport: 'PIPA-compliant workflows with Korean language support.',
  },
  {
    id: 'pdpb-indonesia',
    flag: '🇮🇩',
    name: 'PDP Law',
    fullName: 'Personal Data Protection Law',
    region: 'Indonesia',
    status: 'active',
    effectiveDate: 'October 2024',
    color: '#FF0000',
    keyPoints: {
      consentAge: '17 years',
      penalty: '2% global turnover',
      dpoRequired: 'For specific processing',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Obtain information about data processing' },
      { icon: '✏️', title: 'Right to Rectification', description: 'Update or correct personal data' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Delete personal data' },
      { icon: '↩️', title: 'Right to Withdraw', description: 'Withdraw consent at any time' },
      { icon: '🚫', title: 'Right to Object', description: 'Object to automated decision-making' },
      { icon: '💰', title: 'Right to Compensation', description: 'Seek compensation for violations' },
    ],
    minorsConsent: {
      ageOfConsent: '17 years (Indonesian legal age)',
      parentalConsentRequired: 'Yes - for children under legal age',
      verificationRequired: 'Yes - verification of parental consent',
      specialRules: [
        'Children\'s data classified as specific personal data',
        'Enhanced protection for minor data processing',
        'Parental consent must be verifiable',
        'Two-year transition period for full compliance',
      ],
    },
    platformSupport: 'Indonesia PDP Law compliant consent management.',
  },
  {
    id: 'privacy-act-australia',
    flag: '🇦🇺',
    name: 'Privacy Act',
    fullName: 'Privacy Act 1988 (as amended)',
    region: 'Australia',
    status: 'active',
    effectiveDate: '1988 (amended 2024)',
    color: '#00008B',
    keyPoints: {
      consentAge: 'Case-by-case assessment',
      penalty: 'A$50M or 30% turnover',
      dpoRequired: 'No (but recommended)',
      consentType: 'Contextual consent',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal information held' },
      { icon: '✏️', title: 'Right to Correction', description: 'Request correction of information' },
      { icon: '📢', title: 'Right to Complain', description: 'Lodge complaint with OAIC' },
      { icon: '🚫', title: 'Right to Opt-out', description: 'Opt out of direct marketing' },
      { icon: '❓', title: 'Right to Anonymity', description: 'Option to not identify yourself' },
    ],
    minorsConsent: {
      ageOfConsent: 'No fixed age - capacity based',
      parentalConsentRequired: 'Depends on child\'s capacity to understand',
      verificationRequired: 'Reasonable steps required',
      specialRules: [
        'No fixed age threshold - based on individual capacity',
        'Generally 15+ can consent if they understand implications',
        'Under 15 typically requires parental consent',
        'Children\'s Online Privacy Code coming in 2025',
        'eSafety Commissioner regulates online safety for children',
      ],
    },
    platformSupport: 'Australian Privacy Principles (APP) compliant consent.',
  },
  {
    id: 'pdp-malaysia',
    flag: '🇲🇾',
    name: 'PDPA Malaysia',
    fullName: 'Personal Data Protection Act 2010',
    region: 'Malaysia',
    status: 'active',
    effectiveDate: 'November 2013',
    color: '#010066',
    keyPoints: {
      consentAge: '18 years',
      penalty: 'RM500,000 or 3 years prison',
      dpoRequired: 'No',
      consentType: 'Opt-in consent',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data held' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct inaccurate data' },
      { icon: '↩️', title: 'Right to Withdraw', description: 'Withdraw consent for processing' },
      { icon: '🚫', title: 'Right to Prevent', description: 'Prevent processing causing damage' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - for persons under 18',
      verificationRequired: 'Yes - must verify guardian consent',
      specialRules: [
        'Persons under 18 cannot give valid consent',
        'Parent/guardian must consent on behalf of minor',
        'Organizations must verify guardian relationship',
        'No specific enhanced protections for children in current law',
      ],
    },
    platformSupport: 'Malaysia PDPA compliant consent collection.',
  },
  {
    id: 'pdp-philippines',
    flag: '🇵🇭',
    name: 'DPA',
    fullName: 'Data Privacy Act of 2012',
    region: 'Philippines',
    status: 'active',
    effectiveDate: 'September 2012',
    color: '#0038A8',
    keyPoints: {
      consentAge: '18 years',
      penalty: '₱5M + imprisonment',
      dpoRequired: 'Yes (DPO required)',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '📋', title: 'Right to be Informed', description: 'Be informed of data processing' },
      { icon: '👁️', title: 'Right to Access', description: 'Reasonable access to personal data' },
      { icon: '✏️', title: 'Right to Rectification', description: 'Dispute and correct inaccuracies' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Suspend, withdraw, or block data' },
      { icon: '💰', title: 'Right to Damages', description: 'Be indemnified for damages' },
      { icon: '📦', title: 'Right to Portability', description: 'Obtain data in electronic format' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - for minors under 18',
      verificationRequired: 'Yes - must verify parental consent',
      specialRules: [
        'Minors cannot give valid consent alone',
        'Consent of parent/legal guardian required',
        'Processing of minor data requires extra safeguards',
        'Criminal penalties for unauthorized processing of minor data',
      ],
    },
    platformSupport: 'Philippines DPA compliant consent management.',
  },
  {
    id: 'pdp-vietnam',
    flag: '🇻🇳',
    name: 'PDPD',
    fullName: 'Personal Data Protection Decree',
    region: 'Vietnam',
    status: 'active',
    effectiveDate: 'July 2023',
    color: '#DA251D',
    keyPoints: {
      consentAge: '16 years',
      penalty: 'VND100M (admin) + criminal',
      dpoRequired: 'For certain sectors',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct inaccurate data' },
      { icon: '🗑️', title: 'Right to Deletion', description: 'Delete personal data' },
      { icon: '⏸️', title: 'Right to Restriction', description: 'Restrict data processing' },
      { icon: '🚫', title: 'Right to Object', description: 'Object to certain processing' },
      { icon: '📦', title: 'Right to Portability', description: 'Transfer data electronically' },
    ],
    minorsConsent: {
      ageOfConsent: '16 years (for "basic" consent)',
      parentalConsentRequired: 'Yes - for children under 16',
      verificationRequired: 'Yes - verification required',
      specialRules: [
        'Children under 7: parent/guardian consent only',
        'Children 7-16: parental consent required',
        '16+ can consent independently for non-sensitive data',
        'Sensitive data requires parental consent until 18',
      ],
    },
    platformSupport: 'Vietnam PDPD compliant consent workflows.',
  },
  // ===== MIDDLE EAST =====
  {
    id: 'pdpl-uae',
    flag: '🇦🇪',
    name: 'PDPL',
    fullName: 'Personal Data Protection Law',
    region: 'UAE',
    status: 'active',
    effectiveDate: 'January 2022',
    color: '#00732F',
    keyPoints: {
      consentAge: '21 years',
      penalty: 'AED 5M',
      dpoRequired: 'For specific cases',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data held' },
      { icon: '✏️', title: 'Right to Rectification', description: 'Correct inaccurate data' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Delete personal data' },
      { icon: '⏸️', title: 'Right to Restriction', description: 'Restrict processing' },
      { icon: '📦', title: 'Right to Portability', description: 'Transfer data to another controller' },
      { icon: '⏹️', title: 'Right to Stop', description: 'Stop processing for direct marketing' },
    ],
    minorsConsent: {
      ageOfConsent: '21 years (UAE legal age)',
      parentalConsentRequired: 'Yes - guardian consent for minors',
      verificationRequired: 'Yes - verification of guardian status',
      specialRules: [
        'Age of majority in UAE is 21 years',
        'Guardian consent required for all minors',
        'Processing minor data requires additional safeguards',
        'Free zone laws (DIFC, ADGM) may have different rules',
      ],
    },
    platformSupport: 'UAE PDPL compliant consent management.',
  },
  {
    id: 'pdpl-saudi',
    flag: '🇸🇦',
    name: 'PDPL',
    fullName: 'Personal Data Protection Law',
    region: 'Saudi Arabia',
    status: 'active',
    effectiveDate: 'September 2023',
    color: '#006C35',
    keyPoints: {
      consentAge: '18 years',
      penalty: 'SAR 5M + criminal',
      dpoRequired: 'For certain processors',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct inaccurate data' },
      { icon: '🗑️', title: 'Right to Destruction', description: 'Destroy personal data' },
      { icon: '↩️', title: 'Right to Withdraw', description: 'Withdraw consent' },
      { icon: '📢', title: 'Right to Complain', description: 'File complaints with authority' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - legal guardian consent',
      verificationRequired: 'Yes - must verify guardian',
      specialRules: [
        'Minors cannot provide valid consent',
        'Legal guardian must consent on behalf',
        'Processing minor data needs additional safeguards',
        'Criminal penalties for violations involving minors',
      ],
    },
    platformSupport: 'Saudi PDPL compliant consent with Arabic support.',
  },
  {
    id: 'pdpl-qatar',
    flag: '🇶🇦',
    name: 'PDPL Qatar',
    fullName: 'Personal Data Privacy Law',
    region: 'Qatar',
    status: 'active',
    effectiveDate: '2016 (National) / 2024 (QFC)',
    color: '#8D1B3D',
    keyPoints: {
      consentAge: '18 years',
      penalty: 'QAR 5M',
      dpoRequired: 'For certain entities',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal information' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct inaccurate data' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Erase personal data' },
      { icon: '⏸️', title: 'Right to Restrict', description: 'Restrict processing' },
      { icon: '📦', title: 'Right to Portability', description: 'Port data to another entity' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - guardian consent required',
      verificationRequired: 'Yes - verification measures needed',
      specialRules: [
        'Guardian consent required for minors',
        'QFC has separate data protection regulations',
        'Enhanced protection for sensitive data of minors',
        'National vs. QFC laws may differ in application',
      ],
    },
    platformSupport: 'Qatar PDPL compliant consent management.',
  },
  // ===== AFRICA =====
  {
    id: 'popia',
    flag: '🇿🇦',
    name: 'POPIA',
    fullName: 'Protection of Personal Information Act',
    region: 'South Africa',
    status: 'active',
    effectiveDate: 'July 2021',
    color: '#007A4D',
    keyPoints: {
      consentAge: '18 years',
      penalty: 'ZAR 10M or 10 years prison',
      dpoRequired: 'Yes (Information Officer)',
      consentType: 'Opt-in consent',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal information' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct or delete inaccurate data' },
      { icon: '🗑️', title: 'Right to Deletion', description: 'Delete personal information' },
      { icon: '🚫', title: 'Right to Object', description: 'Object to processing' },
      { icon: '📢', title: 'Right to Complain', description: 'Lodge complaint with Regulator' },
      { icon: '🤖', title: 'Right Against Profiling', description: 'Not be subject to automated decisions' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - competent person must consent',
      verificationRequired: 'Yes - must verify authority',
      specialRules: [
        'Children\'s data is special personal information',
        'Competent person (parent/guardian) must consent',
        'Enhanced protection for children\'s information',
        'Information Officer must ensure child protection',
      ],
    },
    platformSupport: 'POPIA compliant consent with South African language support.',
  },
  {
    id: 'ndpr-nigeria',
    flag: '🇳🇬',
    name: 'NDPA',
    fullName: 'Nigeria Data Protection Act',
    region: 'Nigeria',
    status: 'active',
    effectiveDate: 'June 2023',
    color: '#008751',
    keyPoints: {
      consentAge: '18 years',
      penalty: '2% global turnover or ₦10M',
      dpoRequired: 'For certain controllers',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data' },
      { icon: '✏️', title: 'Right to Rectification', description: 'Rectify inaccurate data' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Erase personal data' },
      { icon: '📦', title: 'Right to Portability', description: 'Port data to another controller' },
      { icon: '🚫', title: 'Right to Object', description: 'Object to processing' },
      { icon: '↩️', title: 'Right to Withdraw', description: 'Withdraw consent' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - for persons under 18',
      verificationRequired: 'Yes - verification required',
      specialRules: [
        'Parental/guardian consent required for minors',
        'Processing children\'s data needs specific safeguards',
        'Must consider best interests of the child',
        'Replaced previous NDPR regulations in 2023',
      ],
    },
    platformSupport: 'Nigeria NDPA compliant consent management.',
  },
  {
    id: 'dpl-kenya',
    flag: '🇰🇪',
    name: 'DPA',
    fullName: 'Data Protection Act',
    region: 'Kenya',
    status: 'active',
    effectiveDate: 'November 2019',
    color: '#006600',
    keyPoints: {
      consentAge: '18 years',
      penalty: 'KES 5M or 10 years prison',
      dpoRequired: 'For certain controllers',
      consentType: 'Opt-in consent',
    },
    rights: [
      { icon: '📋', title: 'Right to be Informed', description: 'Be informed about data collection' },
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct false or misleading data' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Delete personal data' },
      { icon: '📦', title: 'Right to Portability', description: 'Transfer data to another' },
      { icon: '🚫', title: 'Right to Object', description: 'Object to processing' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - parent/guardian consent',
      verificationRequired: 'Yes - reasonable verification',
      specialRules: [
        'Children\'s data is sensitive personal data',
        'Parental consent required for processing',
        'Best interests of child must be considered',
        'Additional protections for educational data',
      ],
    },
    platformSupport: 'Kenya DPA compliant consent workflows.',
  },
  // ===== AMERICAS =====
  {
    id: 'pipeda',
    flag: '🇨🇦',
    name: 'PIPEDA',
    fullName: 'Personal Information Protection and Electronic Documents Act',
    region: 'Canada',
    status: 'active',
    effectiveDate: '2000 (amended ongoing)',
    color: '#FF0000',
    keyPoints: {
      consentAge: 'Varies by province',
      penalty: 'C$100,000 per violation',
      dpoRequired: 'Yes (Privacy Officer)',
      consentType: 'Meaningful consent',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal information' },
      { icon: '✏️', title: 'Right to Correction', description: 'Challenge accuracy and completeness' },
      { icon: '↩️', title: 'Right to Withdraw', description: 'Withdraw consent' },
      { icon: '📢', title: 'Right to Complain', description: 'File complaint with Privacy Commissioner' },
    ],
    minorsConsent: {
      ageOfConsent: 'Varies by province (13-19)',
      parentalConsentRequired: 'Depends on age and province',
      verificationRequired: 'Yes - meaningful consent required',
      specialRules: [
        'Age of consent varies: 13 (most provinces) to 19 (BC, NS, NB)',
        'Must assess child\'s capacity to understand',
        'COPPA-like provisions for online services',
        'Quebec has specific provisions under Law 25',
        'Bill C-27 proposes new child protection measures',
      ],
    },
    platformSupport: 'PIPEDA compliant consent with bilingual (EN/FR) support.',
  },
  {
    id: 'lfpdppp-mexico',
    flag: '🇲🇽',
    name: 'LFPDPPP',
    fullName: 'Federal Law on Protection of Personal Data Held by Private Parties',
    region: 'Mexico',
    status: 'active',
    effectiveDate: 'July 2010',
    color: '#006847',
    keyPoints: {
      consentAge: '18 years',
      penalty: 'MXN 52M',
      dpoRequired: 'Yes',
      consentType: 'Express consent for sensitive',
    },
    rights: [
      { icon: '👁️', title: 'Acceso (Access)', description: 'Access personal data' },
      { icon: '✏️', title: 'Rectificación (Rectification)', description: 'Correct inaccurate data' },
      { icon: '🗑️', title: 'Cancelación (Cancellation)', description: 'Delete personal data' },
      { icon: '🚫', title: 'Oposición (Opposition)', description: 'Object to processing' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - parent/tutor consent',
      verificationRequired: 'Yes - verification of authority',
      specialRules: [
        'Minors cannot provide valid consent alone',
        'Parent or legal tutor must consent',
        'ARCO rights can be exercised by representative',
        'Privacy notice must be understandable to minors',
      ],
    },
    platformSupport: 'Mexico LFPDPPP compliant with Spanish language support.',
  },
  {
    id: 'pdpa-argentina',
    flag: '🇦🇷',
    name: 'PDPA',
    fullName: 'Personal Data Protection Act (Ley 25.326)',
    region: 'Argentina',
    status: 'active',
    effectiveDate: 'October 2000',
    color: '#75AADB',
    keyPoints: {
      consentAge: '13-18 years',
      penalty: 'ARS 100,000',
      dpoRequired: 'No',
      consentType: 'Express written consent',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data' },
      { icon: '✏️', title: 'Right to Rectification', description: 'Update or correct data' },
      { icon: '🗑️', title: 'Right to Suppression', description: 'Delete personal data' },
      { icon: '🔒', title: 'Right to Confidentiality', description: 'Confidential treatment of data' },
    ],
    minorsConsent: {
      ageOfConsent: '13-18 years (tiered)',
      parentalConsentRequired: 'Yes - for under 13, partial 13-18',
      verificationRequired: 'Yes - reasonable verification',
      specialRules: [
        'Under 13: full parental consent required',
        '13-18: can consent for non-sensitive data',
        'Sensitive data requires parental consent until 18',
        'EU adequacy decision recognizes Argentina\'s protection',
      ],
    },
    platformSupport: 'Argentina PDPA compliant with Spanish support.',
  },
  {
    id: 'pdp-chile',
    flag: '🇨🇱',
    name: 'Law 19.628',
    fullName: 'Law on Protection of Private Life (as amended)',
    region: 'Chile',
    status: 'active',
    effectiveDate: '1999 (major reform 2024)',
    color: '#0039A6',
    keyPoints: {
      consentAge: '14 years',
      penalty: 'UTM 20,000 (~USD 1.5M)',
      dpoRequired: 'For certain entities',
      consentType: 'Explicit consent',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data' },
      { icon: '✏️', title: 'Right to Rectification', description: 'Correct inaccurate data' },
      { icon: '🗑️', title: 'Right to Cancellation', description: 'Delete personal data' },
      { icon: '🚫', title: 'Right to Opposition', description: 'Object to processing' },
      { icon: '📦', title: 'Right to Portability', description: 'Port data (new in 2024)' },
    ],
    minorsConsent: {
      ageOfConsent: '14 years (under new law)',
      parentalConsentRequired: 'Yes - for under 14',
      verificationRequired: 'Yes - verification required',
      specialRules: [
        'Major reform in 2024 modernized child provisions',
        'Children 14+ can consent for certain processing',
        'Under 14 requires parental/guardian consent',
        'New law creates independent Data Protection Agency',
      ],
    },
    platformSupport: 'Chile data protection law compliant consent.',
  },
  {
    id: 'pdp-colombia',
    flag: '🇨🇴',
    name: 'Law 1581',
    fullName: 'Statutory Law 1581 of 2012',
    region: 'Colombia',
    status: 'active',
    effectiveDate: 'October 2012',
    color: '#FCD116',
    keyPoints: {
      consentAge: '18 years',
      penalty: 'COP 2,000 SMLMV (~USD 500K)',
      dpoRequired: 'No (but recommended)',
      consentType: 'Express prior consent',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Know, update, and rectify data' },
      { icon: '✏️', title: 'Right to Update', description: 'Update personal information' },
      { icon: '🗑️', title: 'Right to Suppression', description: 'Request deletion' },
      { icon: '↩️', title: 'Right to Revoke', description: 'Revoke authorization' },
      { icon: '📢', title: 'Right to Complain', description: 'File complaints with SIC' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years',
      parentalConsentRequired: 'Yes - for children and adolescents',
      verificationRequired: 'Yes - must verify authorization',
      specialRules: [
        'Children\'s data classified as sensitive',
        'Processing must respect best interests of child',
        'Constitutional Court has reinforced protections',
        'Special safeguards for biometric data of minors',
      ],
    },
    platformSupport: 'Colombia Law 1581 compliant with Spanish support.',
  },
  // ===== EUROPE (Non-EU) =====
  {
    id: 'uk-gdpr',
    flag: '🇬🇧',
    name: 'UK GDPR',
    fullName: 'UK General Data Protection Regulation',
    region: 'United Kingdom',
    status: 'active',
    effectiveDate: 'January 2021',
    color: '#012169',
    keyPoints: {
      consentAge: '13 years',
      penalty: '£17.5M or 4% turnover',
      dpoRequired: 'For specific processing',
      consentType: 'Explicit Opt-in',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data (Subject Access Request)' },
      { icon: '✏️', title: 'Right to Rectification', description: 'Correct inaccurate data' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Right to be forgotten' },
      { icon: '⏸️', title: 'Right to Restriction', description: 'Restrict processing' },
      { icon: '📦', title: 'Right to Portability', description: 'Data portability' },
      { icon: '🚫', title: 'Right to Object', description: 'Object to processing' },
    ],
    minorsConsent: {
      ageOfConsent: '13 years (lower than EU GDPR)',
      parentalConsentRequired: 'Yes - for children under 13',
      verificationRequired: 'Yes - reasonable efforts',
      specialRules: [
        'UK set age at 13 (lowest allowed under EU GDPR)',
        'Age Appropriate Design Code (Children\'s Code) applies',
        'Online services must consider best interests of children',
        '15 standards for child-friendly online services',
        'ICO actively enforces children\'s code',
      ],
    },
    platformSupport: 'UK GDPR and Children\'s Code compliant consent.',
  },
  {
    id: 'dsg-switzerland',
    flag: '🇨🇭',
    name: 'nFADP',
    fullName: 'New Federal Act on Data Protection',
    region: 'Switzerland',
    status: 'active',
    effectiveDate: 'September 2023',
    color: '#FF0000',
    keyPoints: {
      consentAge: '18 years',
      penalty: 'CHF 250,000 (personal)',
      dpoRequired: 'Recommended',
      consentType: 'Explicit for sensitive',
    },
    rights: [
      { icon: '👁️', title: 'Right to Information', description: 'Information about data processing' },
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data' },
      { icon: '📦', title: 'Right to Portability', description: 'Data portability' },
      { icon: '✏️', title: 'Right to Rectification', description: 'Correct inaccurate data' },
      { icon: '🗑️', title: 'Right to Erasure', description: 'Delete personal data' },
    ],
    minorsConsent: {
      ageOfConsent: '18 years (Swiss legal age)',
      parentalConsentRequired: 'Yes - for minors',
      verificationRequired: 'Yes - appropriate verification',
      specialRules: [
        'Minors can only consent if they can judge implications',
        'Generally parental consent needed under 18',
        'Revised law brought closer to GDPR standards',
        'Personal liability for executives (CHF 250K)',
      ],
    },
    platformSupport: 'Swiss nFADP compliant consent with multilingual support.',
  },
  // ===== US STATE LAWS =====
  {
    id: 'vcdpa',
    flag: '🇺🇸',
    name: 'VCDPA',
    fullName: 'Virginia Consumer Data Protection Act',
    region: 'Virginia, USA',
    status: 'active',
    effectiveDate: 'January 2023',
    color: '#002F6C',
    keyPoints: {
      consentAge: '13 years',
      penalty: '$7,500 per violation',
      dpoRequired: 'No',
      consentType: 'Opt-out (opt-in for sensitive)',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct inaccuracies' },
      { icon: '🗑️', title: 'Right to Delete', description: 'Delete personal data' },
      { icon: '📦', title: 'Right to Portability', description: 'Data portability' },
      { icon: '🚫', title: 'Right to Opt-out', description: 'Opt out of targeted advertising' },
    ],
    minorsConsent: {
      ageOfConsent: '13 years',
      parentalConsentRequired: 'Yes - parental consent for known children',
      verificationRequired: 'Yes - COPPA-compliant verification',
      specialRules: [
        'Known children under 13 require COPPA compliance',
        'Opt-in consent required for processing minor data',
        'Sensitive data includes data from known children',
        'No private right of action',
      ],
    },
    platformSupport: 'Virginia VCDPA compliant consent management.',
  },
  {
    id: 'cpa-colorado',
    flag: '🇺🇸',
    name: 'CPA',
    fullName: 'Colorado Privacy Act',
    region: 'Colorado, USA',
    status: 'active',
    effectiveDate: 'July 2023',
    color: '#002868',
    keyPoints: {
      consentAge: '13 years',
      penalty: '$20,000 per violation',
      dpoRequired: 'No',
      consentType: 'Opt-out (opt-in for sensitive)',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct inaccuracies' },
      { icon: '🗑️', title: 'Right to Delete', description: 'Delete personal data' },
      { icon: '📦', title: 'Right to Portability', description: 'Data portability' },
      { icon: '🚫', title: 'Right to Opt-out', description: 'Opt out of targeted ads and sale' },
    ],
    minorsConsent: {
      ageOfConsent: '13 years',
      parentalConsentRequired: 'Yes - for known children under 13',
      verificationRequired: 'Yes - COPPA standards apply',
      specialRules: [
        'Children 13-16 can consent with parental guidance',
        'Universal opt-out mechanism required by 2024',
        'Data from known children is sensitive data',
        'AG enforcement with 60-day cure period',
      ],
    },
    platformSupport: 'Colorado CPA compliant consent workflows.',
  },
  {
    id: 'ctdpa',
    flag: '🇺🇸',
    name: 'CTDPA',
    fullName: 'Connecticut Data Privacy Act',
    region: 'Connecticut, USA',
    status: 'active',
    effectiveDate: 'July 2023',
    color: '#0C2340',
    keyPoints: {
      consentAge: '13 years',
      penalty: '$5,000 per violation',
      dpoRequired: 'No',
      consentType: 'Opt-out model',
    },
    rights: [
      { icon: '👁️', title: 'Right to Access', description: 'Access personal data' },
      { icon: '✏️', title: 'Right to Correction', description: 'Correct inaccuracies' },
      { icon: '🗑️', title: 'Right to Delete', description: 'Delete personal data' },
      { icon: '📦', title: 'Right to Portability', description: 'Data portability' },
      { icon: '🚫', title: 'Right to Opt-out', description: 'Opt out of sale and profiling' },
    ],
    minorsConsent: {
      ageOfConsent: '13 years',
      parentalConsentRequired: 'Yes - for children under 13',
      verificationRequired: 'Yes - COPPA compliance required',
      specialRules: [
        'Known child data is sensitive personal data',
        'Opt-in consent required for sensitive data',
        'Must honor universal opt-out signals',
        'COPPA framework applies to under 13',
      ],
    },
    platformSupport: 'Connecticut CTDPA compliant consent.',
  },
];

// Group laws by region for better organization
const lawRegions = [
  { name: 'Asia-Pacific', laws: ['dpdp', 'pdpa-singapore', 'pdpa-thailand', 'appi-japan', 'pipl-china', 'pipa-korea', 'pdpb-indonesia', 'privacy-act-australia', 'pdp-malaysia', 'pdp-philippines', 'pdp-vietnam'] },
  { name: 'Europe', laws: ['gdpr', 'uk-gdpr', 'dsg-switzerland'] },
  { name: 'Middle East', laws: ['pdpl-uae', 'pdpl-saudi', 'pdpl-qatar'] },
  { name: 'Africa', laws: ['popia', 'ndpr-nigeria', 'dpl-kenya'] },
  { name: 'Americas', laws: ['ccpa', 'vcdpa', 'cpa-colorado', 'ctdpa', 'pipeda', 'lfpdppp-mexico', 'lgpd', 'pdpa-argentina', 'pdp-chile', 'pdp-colombia'] },
];

// ===== COMPONENT =====

export const PrivacyLawsGuide: React.FC = () => {
  const [expandedLaws, setExpandedLaws] = useState<Set<string>>(new Set());
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const toggleLaw = (lawId: string) => {
    setExpandedLaws(prev => {
      const next = new Set(prev);
      if (next.has(lawId)) {
        next.delete(lawId);
      } else {
        next.add(lawId);
      }
      return next;
    });
  };

  // Filter laws based on region and search
  const filteredLaws = privacyLaws.filter(law => {
    const matchesRegion = selectedRegion === 'all' || 
      lawRegions.find(r => r.name === selectedRegion)?.laws.includes(law.id);
    const matchesSearch = searchTerm === '' || 
      law.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      law.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      law.region.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRegion && matchesSearch;
  });

  return (
    <Container>
      {/* Overview Section */}
      <LawCard>
        <SectionTitle>
          <span className="icon">🌍</span>
          Global Privacy Landscape
        </SectionTitle>
        <p style={{ color: theme.textSecondary, marginBottom: '24px', fontSize: '15px', lineHeight: 1.7 }}>
          Data privacy regulations are evolving worldwide. This guide covers <strong>{privacyLaws.length} privacy laws</strong> across 
          5 regions. Understanding these laws is crucial for organizations collecting personal data.
        </p>
        <InfographicViewer syntax={GLOBAL_PRIVACY_LAWS_SYNTAX} height={200} />
        
        {/* Stats Summary */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '16px', 
          marginTop: '24px',
          padding: '20px',
          background: neutral[1],
          borderRadius: '12px'
        }}>
          {lawRegions.map(region => (
            <div key={region.name} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: theme.primary }}>{region.laws.length}</div>
              <div style={{ fontSize: '13px', color: theme.textSecondary }}>{region.name}</div>
            </div>
          ))}
        </div>
      </LawCard>

      {/* Filter Controls */}
      <LawCard style={{ padding: '20px 32px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: theme.textSecondary, display: 'block', marginBottom: '6px' }}>
              Search Laws
            </label>
            <input
              type="text"
              placeholder="Search by name, region..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: `1px solid ${neutral[4]}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ minWidth: '180px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: theme.textSecondary, display: 'block', marginBottom: '6px' }}>
              Filter by Region
            </label>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: `1px solid ${neutral[4]}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all">All Regions ({privacyLaws.length})</option>
              {lawRegions.map(region => (
                <option key={region.name} value={region.name}>
                  {region.name} ({region.laws.length})
                </option>
              ))}
            </select>
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <span style={{ 
              padding: '10px 16px', 
              background: theme.primary, 
              color: 'white', 
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600
            }}>
              {filteredLaws.length} laws
            </span>
          </div>
        </div>
      </LawCard>

      {/* Individual Law Cards */}
      {filteredLaws.map(law => (
        <LawCard key={law.id}>
          <LawHeader $color={law.color}>
            <FlagIcon>{law.flag}</FlagIcon>
            <LawTitle>
              <h3>{law.name}</h3>
              <p>{law.fullName} • {law.region}</p>
            </LawTitle>
            <StatusBadge $status={law.status}>
              {law.status === 'active' ? '✓ Active' : law.status}
            </StatusBadge>
          </LawHeader>

          <KeyPointsGrid>
            <KeyPoint>
              <h4>Consent Age</h4>
              <p>{law.keyPoints.consentAge}</p>
            </KeyPoint>
            <KeyPoint>
              <h4>Max Penalty</h4>
              <p>{law.keyPoints.penalty}</p>
            </KeyPoint>
            <KeyPoint>
              <h4>DPO Required</h4>
              <p>{law.keyPoints.dpoRequired}</p>
            </KeyPoint>
            <KeyPoint>
              <h4>Consent Model</h4>
              <p>{law.keyPoints.consentType}</p>
            </KeyPoint>
          </KeyPointsGrid>

          <ExpandableSection>
            <ExpandButton onClick={() => toggleLaw(law.id)}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ transform: expandedLaws.has(law.id) ? 'rotate(180deg)' : 'rotate(0)' }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
              {expandedLaws.has(law.id) ? 'Hide Details' : 'View Data Subject Rights'}
            </ExpandButton>

            <ExpandedContent $expanded={expandedLaws.has(law.id)}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: theme.textPrimary }}>
                Data Subject Rights under {law.name}
              </h4>
              <RightsList>
                {law.rights.map((right, index) => (
                  <RightItem key={index}>
                    <span className="icon">{right.icon}</span>
                    <div className="content">
                      <h5>{right.title}</h5>
                      <p>{right.description}</p>
                    </div>
                  </RightItem>
                ))}
              </RightsList>

              {/* Minors Consent Section */}
              <MinorsConsentCard>
                <MinorsConsentHeader>
                  <span className="icon">👶</span>
                  <h4>Consent of Minors under {law.name}</h4>
                </MinorsConsentHeader>
                <MinorsConsentGrid>
                  <MinorsConsentStat>
                    <div className="label">Age of Consent</div>
                    <div className="value">{law.minorsConsent.ageOfConsent}</div>
                  </MinorsConsentStat>
                  <MinorsConsentStat>
                    <div className="label">Parental Consent</div>
                    <div className="value">{law.minorsConsent.parentalConsentRequired}</div>
                  </MinorsConsentStat>
                  <MinorsConsentStat>
                    <div className="label">Verification</div>
                    <div className="value">{law.minorsConsent.verificationRequired}</div>
                  </MinorsConsentStat>
                </MinorsConsentGrid>
                <MinorsRulesList>
                  {law.minorsConsent.specialRules.map((rule, index) => (
                    <li key={index}>{rule}</li>
                  ))}
                </MinorsRulesList>
              </MinorsConsentCard>

              <InfoBox $type="success">
                <span className="icon">✅</span>
                <div className="content">
                  <strong>Platform Support:</strong> {law.platformSupport}
                </div>
              </InfoBox>
            </ExpandedContent>
          </ExpandableSection>
        </LawCard>
      ))}

      {/* Best Practices */}
      <LawCard>
        <SectionTitle>
          <span className="icon">💡</span>
          Compliance Best Practices
        </SectionTitle>
        <RightsList>
          <RightItem>
            <span className="icon">📝</span>
            <div className="content">
              <h5>Use Clear Language</h5>
              <p>Avoid legal jargon. Write consent requests in plain, simple language that anyone can understand.</p>
            </div>
          </RightItem>
          <RightItem>
            <span className="icon">🎯</span>
            <div className="content">
              <h5>Be Specific</h5>
              <p>Clearly state what data you collect, why you need it, and how you'll use it. No bundled consents.</p>
            </div>
          </RightItem>
          <RightItem>
            <span className="icon">✅</span>
            <div className="content">
              <h5>Explicit Consent</h5>
              <p>Use clear opt-in mechanisms. Never use pre-checked boxes or implied consent for personal data.</p>
            </div>
          </RightItem>
          <RightItem>
            <span className="icon">↩️</span>
            <div className="content">
              <h5>Easy Withdrawal</h5>
              <p>Make it as easy to withdraw consent as it was to give it. Provide clear withdrawal mechanisms.</p>
            </div>
          </RightItem>
          <RightItem>
            <span className="icon">📊</span>
            <div className="content">
              <h5>Maintain Records</h5>
              <p>Keep detailed records of consent including when, how, and what was consented to.</p>
            </div>
          </RightItem>
          <RightItem>
            <span className="icon">🔄</span>
            <div className="content">
              <h5>Regular Reviews</h5>
              <p>Periodically review and update consent mechanisms to ensure ongoing compliance.</p>
            </div>
          </RightItem>
        </RightsList>
      </LawCard>
    </Container>
  );
};

export default PrivacyLawsGuide;
